import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { NearestBarracks } from "@/components/NearestBarracks";
import { OutsideAssist } from "@/components/OutsideAssist";
import { ClosedTimeEditor } from "@/components/ClosedTimeEditor";
import { useRealtime } from "@/lib/realtime";
import { useCallPresence } from "@/lib/presence";
import Layout, { PageHeader } from "@/components/Layout";
import DispatchMap from "@/components/DispatchMap";
import { DiscBadge, PriorityBadge, StatusChip, MultiAgencyBadge } from "@/components/badges";
import { toast } from "sonner";
import IncidentEvidence from "@/components/IncidentEvidence";
import MultimediaPanel from "@/components/MultimediaPanel";
import TranscriptionPanel from "@/components/TranscriptionPanel";
import useAddons from "@/hooks/useAddons";
import { Sparkles, Loader2, ShieldCheck, FileOutput, Clock, Ban, FileSearch, ShieldAlert, IdCard, Paperclip, FileText, ExternalLink, Pencil, Check, X, Eye, ArrowRightLeft, Users, MapPin, UserPlus } from "lucide-react";
import HazardBanner from "@/components/HazardBanner";
import { Dial8x8Button } from "@/components/Dial8x8";
import { Scale, FileDown } from "lucide-react";
import { downloadCallEventPdf } from "@/lib/callEventPdf";
import CloseCallDisposition from "@/components/CloseCallDisposition";
import FieldFormModal from "@/components/FieldForms";
import AttachmentStrip from "@/components/AttachmentStrip";
import { useAuth } from "@/context/AuthContext";
import { CallEntities } from "@/components/CallEntities";
import { LocateCaller } from "@/components/LocateCaller";
import { LocateChat } from "@/components/LocateChat";
import { AddressIntelPanel } from "@/components/AddressIntelPanel";
import { CallCorrection } from "@/components/CallCorrection";

const STATUSES = ["pending", "dispatched", "enroute", "active", "onscene", "delayed_onscene", "closed"];
const STATUS_LABELS = { enroute: "EN ROUTE", delayed_onscene: "DELAYED ON SCENE" };
const fmtEta = (s) => (s == null ? null : s < 60 ? `${s}s` : `${Math.round(s / 60)} min`);
const fmtDist = (m) => (m == null ? "" : m < 1609 ? `${Math.round(m)} m` : `${(m / 1609).toFixed(1)} mi`);

export default function IncidentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canDelete = (f) => user && (f.created_by_user_id === user.id ||
    ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user.role));
  const nav = useNavigate();
  const addons = useAddons();
  const [inc, setInc] = useState(null);
  const [note, setNote] = useState("");
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [dispOpen, setDispOpen] = useState(false);
  const [formType, setFormType] = useState(null);
  const [forms, setForms] = useState([]);
  const [reassign, setReassign] = useState(null);
  const [raUnits, setRaUnits] = useState([]);
  const [raBusy, setRaBusy] = useState(false);
  const [unitsList, setUnitsList] = useState([]);
  const [preempt, setPreempt] = useState(null);
  const [pBusy, setPBusy] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editText, setEditText] = useState("");
  // While the operator is actively typing a note/edit, suppress the background
  // refresh so a poll/WebSocket push can't re-render and disrupt the entry.
  const typingRef = useRef(false);
  const typingT = useRef(null);
  const markTyping = useCallback(() => {
    typingRef.current = true;
    clearTimeout(typingT.current);
    typingT.current = setTimeout(() => { typingRef.current = false; }, 2500);
  }, []);
  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`/incidents/${id}`);
      setInc(data);
      client.get("/units").then((r) => setUnitsList(r.data || [])).catch(() => {});
      try {
        const f = await client.get("/forms", { params: { incident_cc: data.cc_number } });
        setForms(f.data || []);
      } catch { /* forms optional */ }
    } catch (e) {
      setLoadErr(e.response?.status === 403 ? "forbidden" : "notfound");
    }
  }, [id]);
  useEffect(() => {
    load();
    const iv = setInterval(() => { if (!loadErr && !typingRef.current) load(); }, 2000);
    return () => clearInterval(iv);
  }, [load, loadErr]);
  useRealtime(["incidents"], () => { if (!loadErr && !typingRef.current) load(); });
  const { viewers, typers, notifyTyping } = useCallPresence({ cc: inc?.cc_number, label: user?.name || "Dispatch", active: !!inc });
  const [custody, setCustody] = useState([]);
  useEffect(() => { if (inc?.cc_number) client.get(`/custody/${inc.cc_number}`).then((r) => setCustody(r.data || [])).catch(() => {}); }, [inc?.cc_number]);
  // Live distance + drive-time ETA for every responding unit (OSRM road route).
  const [etas, setEtas] = useState({});
  const assignedSig = (inc?.assigned_units || []).join(",");
  useEffect(() => {
    if (!id) return;
    let alive = true;
    const loadEtas = async () => {
      try {
        const { data } = await client.get(`/incidents/${id}/responder-etas`);
        if (!alive) return;
        const m = {};
        (data.etas || []).forEach((e) => { m[e.unit] = e; });
        setEtas(m);
      } catch { /* noop */ }
    };
    loadEtas();
    const iv = setInterval(loadEtas, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [id, assignedSig]);

  // Auto Transfer Suggest — flags out-of-jurisdiction calls with the right PSAP contact.
  const [transfer, setTransfer] = useState(null);
  useEffect(() => {
    if (!id) return;
    client.get(`/incidents/${id}/transfer-suggest`).then((r) => setTransfer(r.data)).catch(() => {});
  }, [id, inc?.location?.address]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attached Service of Process records (warrant / subpoena / affidavit workflow) for this CFS.
  const [svc, setSvc] = useState([]);
  useEffect(() => {
    if (!inc?.cc_number) return;
    client.get("/service", { params: { cc: inc.cc_number } }).then((r) => setSvc(r.data)).catch(() => {});
  }, [inc?.cc_number]);

  if (loadErr) return <Layout><div data-testid="incident-load-error" className="p-6 font-mono text-sm text-[#EF4444]">{loadErr === "forbidden" ? "Access denied — this record is not available in your environment." : "Incident not found."}</div></Layout>;
  if (!inc) return <Layout><div className="p-6 font-mono text-sm text-[#9CA3AF]">Loading incident…</div></Layout>;

  const addNote = async () => {
    if (!note.trim()) return;
    await client.post(`/incidents/${id}/events`, { message: note, type: "note" });
    setNote(""); load();
  };
  const canEditNote = (e) => (e.type === "note" || e.type === "field_note") && user &&
    ((e.author_user_id ? e.author_user_id === user.id : false) || ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user.role));
  const saveEdit = async (idx) => {
    if (!editText.trim()) { toast.error("Note text required"); return; }
    try {
      await client.patch(`/incidents/${id}/events/${idx}`, { message: editText });
      toast.success("Log note updated");
      setEditIdx(null); setEditText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update note"); }
  };
  const setStatus = async (s) => {
    if (s === "closed" && inc.discipline === "police") { setDispOpen(true); return; }
    await client.post(`/incidents/${id}/status`, { status: s, note: s === "closed" ? "Cleared by operator" : null });
    toast.success(`Status → ${s.toUpperCase()}`); load();
  };
  const genAi = async () => {
    setAiBusy(true); setAi(null);
    try {
      const { data } = await client.post(`/incidents/${id}/ai-narrative`);
      setAi(data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI error"); }
    finally { setAiBusy(false); }
  };
  const approveAi = async () => {
    await client.post(`/incidents/${id}/save-ai-narrative`, { narrative: ai.narrative });
    toast.success("AI narrative approved & saved"); setAi(null); load();
  };
  const toRms = async () => {
    try {
      const { data } = await client.post(`/incidents/${id}/convert-rms`);
      toast.success(`Created ${data.report_number}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const ackFederalAlert = async () => {
    try { await client.post(`/incidents/${id}/clear-federal-alert`); toast.success("Federal alert acknowledged"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const clearBackup = async () => {
    try { await client.post(`/incidents/${id}/clear-backup`); toast.success("Backup request cleared"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openReassign = async () => {
    setReassign({ from: (inc.assigned_units || [])[0] || "", to: "", note: "" });
    try { const { data } = await client.get("/units"); setRaUnits(data); } catch { setRaUnits([]); }
  };
  const openPreempt = () => setPreempt({ unit: "", note: "" });
  const submitPreempt = async () => {
    if (!preempt.unit) { toast.error("Select a unit to assign"); return; }
    setPBusy(true);
    try {
      const { data } = await client.post(`/incidents/${id}/preempt`, { unit_id: preempt.unit, note: preempt.note });
      toast.success(data.pulled_from ? `${preempt.unit} pre-empted from ${data.pulled_from}` : `${preempt.unit} assigned to this call`);
      setPreempt(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Pre-empt failed"); }
    finally { setPBusy(false); }
  };
  const submitReassign = async () => {
    if (!reassign.to) { toast.error("Select a unit to hand off to"); return; }
    if (!reassign.note.trim()) { toast.error("A handoff note is required"); return; }
    setRaBusy(true);
    try {
      await client.post(`/incidents/${id}/reassign`, { to_unit: reassign.to, from_unit: reassign.from || null, note: reassign.note });
      toast.success(`Call handed off to ${reassign.to}`);
      setReassign(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassignment failed"); }
    finally { setRaBusy(false); }
  };

  return (
    <Layout>
      <PageHeader title={`CASE# ${inc.cc_number}`} sub={`${inc.call_type_label} · ${inc.call_type_code}${inc.public_ref ? ` · CAD# ${inc.public_ref}` : ""}`}
        right={<div className="flex items-center gap-2">
          <button data-testid="incident-call-event-pdf" title="Export government-grade Call Event Report PDF (records request ready)"
            onClick={() => downloadCallEventPdf(inc.id, inc.cc_number)}
            className="px-3 py-1.5 text-xs font-head font-bold bg-[#F59E0B] text-black hover:bg-[#FBBF24] flex items-center gap-1.5"><FileDown size={13} /> EXPORT PDF</button>
          <button data-testid="back-btn" onClick={() => nav(-1)} className="px-3 py-1.5 text-xs font-head bg-[#141518] border border-[#2C2D35] hover:text-white">← Back</button>
        </div>} />
      <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-[1fr_400px]">
        {/* left column */}
        <div className="p-5 border-r border-[#2C2D35] space-y-4">
          {inc.federal_alert && !inc.federal_alert.acknowledged && (
            <div data-testid="incident-federal-alert" className="border-2 border-[#DC2626] bg-[#DC2626]/10 p-3 animate-pulse">
              <div className="flex items-center gap-2 text-[#DC2626] font-head font-bold text-sm"><ShieldAlert size={16} /> 🚨 {inc.federal_alert.kind === "missing" ? "MISSING / EXPLOITED PERSON ALERT" : "FEDERAL FUGITIVE ALERT"}</div>
              <div className="text-xs font-mono text-[#fca5a5] mt-1 leading-relaxed">
                {inc.federal_alert.kind === "missing"
                  ? <>FBI Kidnappings &amp; Missing Persons subject(s): {(inc.federal_alert.names || []).join(", ")}. Report sightings to the FBI (1-800-CALL-FBI) or NCMEC (1-800-843-5678).</>
                  : <>NCIC name query "{inc.federal_alert.query_value}" matched FBI Most Wanted: {(inc.federal_alert.names || []).join(", ")}. Confirm with the FBI Field Office before any enforcement action.</>}
              </div>
              <button data-testid="ack-federal-alert" onClick={ackFederalAlert}
                className="mt-2 px-3 py-1.5 text-xs font-head bg-[#DC2626] text-white hover:bg-[#b91c1c] transition-colors">Acknowledge Alert</button>
            </div>
          )}
          {inc.premise_alert && (
            <HazardBanner summary={inc.premise_alert.summary} hazards={inc.premise_alert.hazards}
              fireEmsStaging={inc.premise_alert.fire_ems_staging}
              onAck={async () => { await client.post(`/premises/${inc.premise_alert.premise_id}/ack`, { device: "CAD", incident_cc: inc.cc_number }); toast.success("Hazard acknowledged (logged to event log)"); load(); }} />
          )}
          {inc.backup_requested && (
            <div data-testid="backup-requested-banner" className="flex items-center gap-2 bg-[#DC2626] text-white px-3 py-2.5 rounded font-head font-bold animate-pulse">
              <Users size={18} /> BACKUP REQUESTED by {inc.backup_requested.by} · {new Date(inc.backup_requested.ts).toLocaleTimeString("en-GB", { hour12: false })}
              <button data-testid="clear-backup-btn" onClick={clearBackup} className="ml-auto px-2.5 py-1 text-xs bg-black/25 hover:bg-black/40 rounded transition-colors">Clear</button>
            </div>
          )}
          {inc.repeat_address && (
            <div data-testid="repeat-address-banner" className="flex items-center gap-2 bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] px-3 py-2 rounded font-head font-bold text-sm">
              <MapPin size={16} /> REPEAT ADDRESS — {inc.repeat_address.count} calls for service at this location ({inc.repeat_address.prior} prior)
              <button data-testid="repeat-address-history" onClick={() => nav(`/premise?address=${encodeURIComponent(inc.location.address)}`)} className="ml-auto px-2.5 py-1 text-xs bg-[#F59E0B]/25 hover:bg-[#F59E0B]/40 rounded transition-colors">View History</button>
            </div>
          )}
          {transfer?.out_of_area && (
            <div data-testid="transfer-suggest-banner" className="bg-[#22D3EE]/10 border border-[#22D3EE]/50 rounded px-3 py-2.5">
              <div className="flex items-center gap-2 text-[#22D3EE] font-head font-bold text-sm">
                <MapPin size={16} /> OUT-OF-AREA — {transfer.state_name?.toUpperCase()} ({transfer.state}) · TRANSFER TO JURISDICTIONAL PSAP
                <button data-testid="transfer-open-directory" onClick={() => nav("/directory")} className="ml-auto px-2.5 py-1 text-xs bg-[#22D3EE]/20 hover:bg-[#22D3EE]/35 rounded transition-colors">Full Directory</button>
              </div>
              <div className="mt-1.5 space-y-1">
                {(transfer.matches || []).map((m, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`transfer-match-${i}`}>
                    <span className="font-mono text-[9px] uppercase text-[#22D3EE]/70 border border-[#22D3EE]/30 px-1 py-px rounded-sm">{m.category}</span>
                    <span className="text-xs text-white">{m.name}</span>
                    {(m.phones || []).slice(0, 2).map((p) => (
                      <span key={p.number} className="inline-flex items-center gap-1.5">
                        <a href={`tel:${p.number.replace(/[^\d+]/g, "")}`} data-testid={`transfer-call-${p.number.replace(/[^\d]/g, "")}`}
                          className="font-mono text-xs text-[#4ade80] hover:underline">{p.label}: {p.number}</a>
                        <Dial8x8Button phone={p.number} targetName={m.name} incidentCc={inc.cc_number} compact />
                      </span>
                    ))}
                    {(m.phones || []).length === 0 && m.website && (
                      <a href={m.website} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-[#22D3EE] hover:underline">registry lookup</a>
                    )}
                  </div>
                ))}
              </div>
              <div className="font-mono text-[9px] text-[#5f7396] mt-1.5">Public/admin numbers are not validated 24x7 transfer lines — confirm via NENA EPRC before emergency transfer.</div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <DiscBadge discipline={inc.discipline} />{inc.multi_agency && <MultiAgencyBadge />}<PriorityBadge priority={inc.priority} /><StatusChip status={inc.status} />
            <span className="font-mono text-[11px] text-[#9CA3AF] ml-auto" data-testid="incident-generated-at">Received {inc.created_at ? new Date(inc.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—"} · Opened by {inc.created_by}</span>
          </div>
          <ClosedTimeEditor incident={inc} onSaved={load} />
          {inc.late_entry && <div data-testid="incident-late-entry" className="font-mono text-[11px] text-[#FCD34D] flex flex-wrap items-center gap-2"><span className="px-1.5 py-0.5 border border-[#F59E0B]/60 bg-[#F59E0B]/15 font-bold text-[9px]">⏮ LATE ENTRY</span><span>Backdated · entered {new Date(inc.late_entry.entered_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })} by {inc.late_entry.entered_by} — {inc.late_entry.reason}{inc.late_entry.recording_ref ? ` · Recording: ${inc.late_entry.recording_ref}` : ""}</span></div>}

          {/* status control */}
          <div className="flex gap-1 flex-wrap">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`set-status-${s}`} onClick={() => setStatus(s)}
                className={`px-3 py-1.5 text-xs font-head border transition-colors ${inc.status === s ? (s === "delayed_onscene" ? "bg-[#EA580C] border-[#EA580C] text-white" : "bg-[#3B82F6] border-[#3B82F6] text-white") : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF] hover:text-white"}`}>
                {STATUS_LABELS[s] || s.toUpperCase()}
              </button>
            ))}
            {user?.role !== "pa_state_constable" && inc.status !== "closed" && (
              <button data-testid="reassign-btn" onClick={openReassign}
                className="px-3 py-1.5 text-xs font-head border border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors flex items-center gap-1.5">
                <ArrowRightLeft size={13} /> Handoff / Reassign
              </button>
            )}
            {user?.role !== "pa_state_constable" && inc.status !== "closed" && (
              <button data-testid="preempt-btn" onClick={openPreempt}
                className="px-3 py-1.5 text-xs font-head border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors flex items-center gap-1.5">
                <UserPlus size={13} /> Pre-empt / Add Unit
              </button>
            )}
          </div>

          {/* location & caller */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#141518] border border-[#2C2D35] p-3">
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1">Location</div>
              <div className="text-sm font-medium">{inc.location.common_name && <span className="text-[#FCD34D]" data-testid="incident-common-name">{inc.location.common_name} · </span>}{inc.location.address}</div>
              {(inc.location.beat || inc.location.district || inc.location.jurisdiction) && <div className="font-mono text-[10px] text-[#9CA3AF] flex flex-wrap items-center gap-2" data-testid="incident-beat-line">{[inc.location.beat && `Beat ${inc.location.beat}`, inc.location.district && `Dist. ${inc.location.district}`, inc.location.jurisdiction].filter(Boolean).join(" · ")}
                {inc.location.jurisdiction && !/State Police/i.test(inc.location.jurisdiction) && inc.status !== "closed" && <OutsideAssist incident={inc} agency={inc.location.jurisdiction} phone={inc.agency_route?.psap?.phone || null} />}</div>}
              <NearestBarracks incident={inc} />
              {inc.location.apt && <div className="font-mono text-[11px] text-[#9CA3AF]">Unit {inc.location.apt}</div>}
              <button data-testid="view-premise-btn" onClick={() => nav(`/premise?address=${encodeURIComponent(inc.location.address)}`)}
                className="mt-2 text-[11px] font-head text-[#3B82F6] hover:underline">View premise history →</button>
              <div className="mt-2"><CallCorrection incident={inc} onChanged={load} /></div>
            </div>
            <div className="bg-[#141518] border border-[#2C2D35] p-3">
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1">Caller</div>
              <div className="text-sm font-medium">{inc.caller?.name || "Anonymous"}</div>
              <div className="font-mono text-[11px] text-[#9CA3AF]">{inc.caller?.phone || "—"}</div>
              {(inc.additional_callers || []).length > 0 && (
                <div data-testid="additional-callers" className="mt-1.5 space-y-0.5">
                  <div className="font-mono text-[9px] text-[#F59E0B] uppercase">Additional callers ({inc.additional_callers.length})</div>
                  {inc.additional_callers.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 font-mono text-[10px] text-[#E5E7EB]">
                      <span className="flex-1 min-w-0 truncate">{c.name || "Unknown"} <span className="text-[#9CA3AF]">{c.phone}{c.note ? ` — ${c.note}` : ""} · {(c.ts || "").slice(11, 16)}Z</span></span>
                      {["supervisor", "watch_commander", "admin"].includes(user?.role) && (
                        <button data-testid={`detach-caller-${i}`} title="Wrong call? Detach this caller and spin them into their own call"
                          onClick={async () => { try { const r = await client.post(`/incidents/${inc.id}/detach-caller`, { index: i }); toast.success(`Caller detached → new call ${r.data.cc_number}`); nav(`/incident/${r.data.new_id}`); } catch (e) { toast.error(e.response?.data?.detail || "Detach failed"); } }}
                          className="px-1.5 py-0.5 font-head font-bold text-[9px] border border-[#F59E0B]/60 text-[#FCD34D] hover:bg-[#F59E0B]/20">DETACH → NEW CALL</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="font-mono text-[11px] text-[#9CA3AF] mt-1">Units: {(inc.assigned_units || []).join(", ") || "unassigned"}</div>
              <LocateCaller incident={inc} />
              <LocateChat incident={inc} />
            </div>
          </div>

          {/* Responding units roster — primary vs self-dispatched backup + live count */}
          <div className="bg-[#141518] border border-[#2C2D35] p-3" data-testid="responding-units-panel">
            <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-2 flex items-center gap-2">
              <Users size={12} /> Responding Units
              <span data-testid="responding-count" className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ background: (inc.assigned_units || []).length ? "#10B981" : "#2C2D35", color: "#fff" }}>
                {(inc.assigned_units || []).length} RESPONDING
              </span>
            </div>
            {(inc.assigned_units || []).length === 0
              ? <div className="text-[11px] font-mono text-[#6B7280]">No units assigned yet.</div>
              : <div className="space-y-1.5">
                  {(inc.assigned_units || []).map((uid) => {
                    const u = unitsList.find((x) => x.unit_id === uid);
                    const isBackup = (inc.backup_units || []).includes(uid);
                    return (
                      <div key={uid} data-testid={`responding-unit-${uid}`} className="flex items-center gap-2 text-[11px] font-mono">
                        <span className="font-bold text-white">{uid}</span>
                        <span className="px-1 rounded text-[9px] font-bold"
                          style={isBackup ? { background: "#F59E0B22", color: "#F59E0B", border: "1px solid #F59E0B55" } : { background: "#3B82F622", color: "#6ea8ff", border: "1px solid #3B82F655" }}>
                          {isBackup ? "BACKUP" : "PRIMARY"}
                        </span>
                        <span className="ml-auto flex items-center gap-2">
                          {(() => {
                            const e = etas[uid];
                            if (u?.status === "onscene") return <span data-testid={`responder-eta-${uid}`} className="text-[10px] font-bold text-[#10B981]">ON SCENE</span>;
                            if (e && e.duration_s != null) return <span data-testid={`responder-eta-${uid}`} className="text-[10px] text-[#6ea8ff]">{e.fallback ? "~" : ""}{fmtEta(e.duration_s)} · {fmtDist(e.distance_m)}</span>;
                            return null;
                          })()}
                          {u && <StatusChip status={u.status} />}
                        </span>
                      </div>
                    );
                  })}
                </div>}
          </div>

          {/* dispatch protocol reminder */}
          <div className="bg-[#141518] border border-[#F59E0B]/40 p-3">
            <div className="font-mono text-[10px] text-[#F59E0B] uppercase mb-1">Dispatch Protocol</div>
            <div className="text-xs font-mono leading-relaxed">{inc.dispatch_protocol}</div>
          </div>

          {inc.narrative && (
            <div className="bg-[#141518] border border-[#2C2D35] p-3">
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1">Initial Narrative</div>
              <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed">{inc.narrative}</pre>
            </div>
          )}

          {/* AI narrative */}
          <div className="border border-[#F59E0B]/50 bg-[#141518]">
            <div className="px-3 py-2 border-b border-[#F59E0B]/40 flex items-center gap-2">
              <Sparkles size={14} className="text-[#F59E0B]" />
              <span className="font-head font-semibold text-sm text-[#F59E0B]">APCO/NENA AI NARRATIVE ASSISTANT</span>
              <button data-testid="gen-ai-narrative" onClick={genAi} disabled={aiBusy}
                className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs font-head bg-[#F59E0B] text-black hover:bg-[#d97706] transition-colors disabled:opacity-50">
                {aiBusy && <Loader2 size={13} className="animate-spin" />} Generate
              </button>
            </div>
            <div className="p-3">
              {inc.ai_narrative && !ai && (
                <div>
                  <div className="font-mono text-[10px] text-[#10B981] uppercase mb-1">✓ Approved narrative on file</div>
                  <pre data-testid="saved-ai-narrative" className="text-xs whitespace-pre-wrap font-mono leading-relaxed">{inc.ai_narrative}</pre>
                </div>
              )}
              {ai && (
                <div>
                  <div className="font-mono text-[9px] text-[#F59E0B] uppercase mb-1 border border-[#F59E0B]/40 px-2 py-1 inline-block">{ai.label}</div>
                  <pre data-testid="ai-narrative-output" className="text-xs whitespace-pre-wrap font-mono leading-relaxed mt-2">{ai.narrative}</pre>
                  <button data-testid="approve-ai-narrative" onClick={approveAi}
                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-head bg-[#10B981] text-black hover:bg-[#059669] transition-colors">
                    <ShieldCheck size={13} /> Review & Approve
                  </button>
                </div>
              )}
              {!inc.ai_narrative && !ai && !aiBusy && (
                <div className="text-xs text-[#9CA3AF] font-mono">Generate an APCO/NENA-formatted narrative from the event log. Output is labeled AI-generated and requires operator approval.</div>
              )}
            </div>
          </div>

          <button data-testid="convert-rms-btn" onClick={toRms} disabled={!!inc.rms_report}
            className="flex items-center gap-2 px-3 py-2 text-sm font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#3B82F6] transition-colors disabled:opacity-50">
            <FileOutput size={15} /> {inc.rms_report ? `RMS: ${inc.rms_report}` : "Convert to RMS Report"}
          </button>

          <button data-testid="cfs-report-btn" onClick={async () => {
            try {
              const r = await client.post(`/rms-reports/from-incident/${inc.id}`);
              nav(`/rms-report/${r.data.id}`);
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to open CFS report"); }
          }} className="flex items-center gap-2 px-3 py-2 text-sm font-head text-white transition-colors" style={{ background: "#1d5d90" }}>
            <FileOutput size={15} /> {inc.rms_report ? `OPEN CFS REPORT (${inc.rms_report})` : "GENERATE CFS REPORT"}
          </button>

          <div className="border border-[#2C2D35] bg-[#141518] p-3">
            {addons.multimedia && <div className="mb-3"><MultimediaPanel incident={inc} /></div>}
            {addons.transcription && <div className="mb-3"><TranscriptionPanel incident={inc} /></div>}
            <IncidentEvidence incidentId={inc.id} attachments={inc.attachments || []} onChanged={load} />
            <div className="mt-3 border border-[#2C2D35]" data-testid="chain-of-custody">
              <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center justify-between">
                <span>CHAIN OF CUSTODY · DIGITAL EVIDENCE</span>
                <span className="font-mono text-[10px] text-[#9CA3AF]">{custody.length} events</span>
              </div>
              {custody.length === 0 ? (
                <div className="p-3 font-mono text-[11px] text-[#6B7280]">No evidence access recorded yet. Uploads, views and transfers of files on this call will be logged here.</div>
              ) : (
                <div className="max-h-52 overflow-y-auto">
                  {custody.map((c, i) => (
                    <div key={i} data-testid={`custody-row-${i}`} className="px-3 py-1.5 border-b border-[#20212633] flex items-center gap-2 text-[11px]">
                      <span className={`font-mono text-[9px] px-1.5 py-0.5 uppercase ${c.action === "view" ? "text-[#3B82F6] border border-[#3B82F6]" : c.action === "upload" ? "text-[#10B981] border border-[#10B981]" : "text-[#F59E0B] border border-[#F59E0B]"}`}>{c.action}</span>
                      <span className="font-mono text-[10px] text-[#9CA3AF] whitespace-nowrap">{c.ts ? new Date(c.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—"}</span>
                      <span className="font-medium">{c.user}</span>
                      <span className="font-mono text-[10px] text-[#9CA3AF] truncate ml-auto max-w-[45%]">{c.filename || c.path || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Prior location history (Address Intelligence add-on) */}
          <AddressIntelPanel incident={inc} onChanged={load} />

          {/* Linked persons & vehicles */}
          <CallEntities incident={inc} onChanged={load} />

          {/* Field forms / records */}
          <div className="border border-[#2C2D35] bg-[#141518] p-3">
            <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-2 flex items-center gap-2">
              <Scale size={12} className="text-[#E0B23C]" /> Service of Process (Warrant · Subpoena · Affidavit)
              <button data-testid="incident-new-service-btn" onClick={() => nav(`/service?new=1&cc=${inc.cc_number}`)}
                className="ml-auto px-2.5 py-1 text-[10px] font-head font-bold uppercase bg-[#E0B23C]/15 border border-[#E0B23C]/50 text-[#E0B23C] hover:bg-[#E0B23C]/25 transition-colors">+ New Service</button>
            </div>
            {svc.length === 0 ? (
              <div className="font-mono text-[11px] text-[#6B7280]">No service records attached to this call.</div>
            ) : (
              <div className="space-y-1.5" data-testid="incident-service-list">
                {svc.map((s) => (
                  <div key={s.id} className="w-full flex items-center gap-2 px-3 py-2 bg-[#0d0e11] border-l-2 border-[#E0B23C]">
                    <button data-testid={`incident-service-${s.service_number}`} onClick={() => nav(`/service?sel=${s.id}`)} className="flex items-center gap-2 min-w-0 flex-1 text-left">
                      <span className="font-mono text-[11px] text-[#8299bd]">{s.service_number}</span>
                      <span className="font-mono text-[9px] uppercase border border-[#2C2D35] px-1 py-px text-[#E0B23C]">{s.doc_type}</span>
                      <span className="text-xs text-white truncate">{s.servee_name}</span>
                    </button>
                    {(s.attachments || []).length > 0 && <span className="font-mono text-[9px] text-[#22D3EE]">📎{s.attachments.length}</span>}
                    <label data-testid={`svc-upload-${s.service_number}`} className="cursor-pointer px-1.5 py-0.5 text-[9px] font-mono border border-[#2C2D35] text-[#22D3EE] hover:border-[#22D3EE] rounded-sm">
                      + FILES
                      <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf" className="hidden" onChange={async (e) => {
                        const files = Array.from(e.target.files || []); e.target.value = "";
                        if (!files.length) return;
                        const fd = new FormData(); files.forEach((fl) => fd.append("files", fl));
                        try { await client.post(`/service/${s.id}/attachments`, fd); toast.success(`${files.length} file(s) attached to ${s.service_number}`); load(); }
                        catch (er) { toast.error(typeof er.response?.data?.detail === "string" ? er.response.data.detail : "Upload failed"); }
                      }} />
                    </label>
                    <span className={`font-mono text-[10px] font-bold ${s.status === "served" ? "text-[#4ade80]" : s.status === "pending" ? "text-[#F59E0B]" : "text-[#22D3EE]"}`}>{s.status.replace(/_/g, " ").toUpperCase()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Field forms / records */}
          <div className="border border-[#2C2D35] bg-[#141518] p-3">
            <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-2">Field Forms & Records</div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="form-btn-banning" onClick={() => setFormType("banning")}
                className="flex items-center gap-2 px-3 py-2 text-xs font-head bg-[#0d0e11] border-l-2 border-[#DC2626] hover:bg-[#1a0b0c] transition-colors"><Ban size={14} className="text-[#DC2626]" /> Banning / Trespass</button>
              <button data-testid="form-btn-warrant" onClick={() => setFormType("warrant")}
                className="flex items-center gap-2 px-3 py-2 text-xs font-head bg-[#0d0e11] border-l-2 border-[#F59E0B] hover:bg-[#1a140a] transition-colors"><FileSearch size={14} className="text-[#F59E0B]" /> Search Warrant</button>
              <button data-testid="form-btn-use_of_force" onClick={() => setFormType("use_of_force")}
                className="flex items-center gap-2 px-3 py-2 text-xs font-head bg-[#0d0e11] border-l-2 border-[#EF4444] hover:bg-[#1a0b0c] transition-colors"><ShieldAlert size={14} className="text-[#EF4444]" /> Use of Force</button>
              <button data-testid="form-btn-fi_card" onClick={() => setFormType("fi_card")}
                className="flex items-center gap-2 px-3 py-2 text-xs font-head bg-[#0d0e11] border-l-2 border-[#8B5CF6] hover:bg-[#12101a] transition-colors"><IdCard size={14} className="text-[#8B5CF6]" /> FI Card</button>
            </div>
            {forms.length > 0 && (
              <div className="mt-3 space-y-2.5" data-testid="incident-forms">
                {forms.map((f) => (
                  <div key={f.id} className="border border-[#2C2D35] bg-[#0d0e11] p-2.5" data-testid={`incident-form-${f.report_number}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-head text-xs text-white truncate">{f.label} · {f.report_number}</div>
                      <span className="font-mono text-[9px] text-[#9CA3AF] shrink-0">{f.created_by}</span>
                    </div>
                    <AttachmentStrip attachments={f.attachments} label="Photos / Records" formId={f.id} canDelete={canDelete(f)} onChanged={load} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* right column: event log */}
        <div className="flex flex-col bg-[#0d0e11] overflow-hidden">
          <div className="px-3 py-2 border-b border-[#2C2D35] font-head text-xs font-semibold flex items-center gap-2">
            <Clock size={13} /> CHRONOLOGICAL EVENT LOG (AUDITED)
            {viewers.length > 0 && (
              <span data-testid="presence-badge" title={viewers.join(", ")}
                className="ml-auto flex items-center gap-1 text-[10px] font-mono font-normal normal-case text-[#10B981]">
                <Eye size={11} /> {viewers.length} viewing
              </span>
            )}
          </div>
          <div className="h-40 hidden">{/* spacer */}</div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {inc.event_log.map((e, idx) => ({ e, idx })).reverse().map(({ e, idx }) => (
              <div key={idx} data-testid={`event-${idx}`} className="border-l-2 border-[#2C2D35] pl-2">
                <div className="font-mono text-[10px] text-[#9CA3AF] flex items-center gap-1">
                  <span>{new Date(e.ts).toLocaleTimeString("en-GB", { hour12: false })} · {e.type.toUpperCase()} · {e.user}</span>
                  {e.is_correction && <span className="text-[#F59E0B]"> · CORRECTION</span>}
                  {e.edited && <span className="text-[#F59E0B]" title={`Edited by ${e.edited_by || "?"}`}> · EDITED</span>}
                  {editIdx !== idx && canEditNote(e) && (
                    <button data-testid={`event-edit-${idx}`} title="Edit note"
                      onClick={() => { setEditIdx(idx); setEditText(e.message); }}
                      className="ml-auto text-[#9CA3AF] hover:text-[#3B82F6]"><Pencil size={11} /></button>
                  )}
                </div>
                {editIdx === idx ? (
                  <div className="mt-1">
                    <textarea data-testid={`event-edit-input-${idx}`} value={editText} onChange={(ev) => { setEditText(ev.target.value); markTyping(); }} rows={2}
                      className="w-full bg-[#141518] border border-[#3B82F6] px-2 py-1.5 text-xs outline-none font-mono" />
                    <div className="flex gap-1 mt-1">
                      <button data-testid={`event-edit-save-${idx}`} onClick={() => saveEdit(idx)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-head bg-[#10B981] text-black"><Check size={11} /> Save</button>
                      <button data-testid={`event-edit-cancel-${idx}`} onClick={() => { setEditIdx(null); setEditText(""); }}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#2C2D35] text-[#9CA3AF]"><X size={11} /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs font-mono leading-snug">{e.message}</div>
                )}
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-[#2C2D35]">
            {typers.length > 0 && <div data-testid="typing-indicator" className="pb-1 text-[10px] font-mono text-[#3B82F6] animate-pulse">{typers.join(", ")} typing…</div>}
            <textarea data-testid="event-note-input" value={note} onChange={(e) => { setNote(e.target.value); notifyTyping(); }} rows={2}
              placeholder="Add chronological entry…" className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none focus:border-[#3B82F6] font-mono" />
            <button data-testid="add-event-btn" onClick={addNote}
              className="mt-1 w-full py-1.5 text-xs font-head bg-[#3B82F6] hover:bg-[#2563eb] text-white transition-colors">Add Log Entry</button>
          </div>
        </div>
      </div>
      <CloseCallDisposition open={dispOpen} onClose={() => setDispOpen(false)} incidentId={id} incident={inc}
        onDone={() => load()} />
      {preempt && (
        <div data-testid="preempt-modal" className="fixed inset-0 z-[999] flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setPreempt(null)}>
          <div className="mt-20 w-full max-w-md bg-[#0d0e11] border border-[#2C2D35]" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#2C2D35] flex items-center gap-2 font-head text-sm text-white">
              <UserPlus size={15} className="text-[#F59E0B]" /> Pre-empt / Add Unit — {inc.cc_number}
              <button data-testid="preempt-close" onClick={() => setPreempt(null)} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[11px] font-mono text-[#9CA3AF]">Assign any unit to this call. A unit already on another call will be pulled off it (pre-empted).</div>
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Unit *</label>
                <select data-testid="preempt-unit" value={preempt.unit} onChange={(e) => setPreempt({ ...preempt, unit: e.target.value })}
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm font-mono outline-none focus:border-[#F59E0B]">
                  <option value="">— select unit —</option>
                  {unitsList.filter((u) => !(inc.assigned_units || []).includes(u.unit_id)).map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>{u.unit_id} · {u.status}{u.current_incident ? ` · on ${u.current_incident}` : ""}</option>
                  ))}
                </select></div>
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Note (audited, optional)</label>
                <textarea data-testid="preempt-note" value={preempt.note} onChange={(e) => setPreempt({ ...preempt, note: e.target.value })} rows={2}
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#F59E0B]" /></div>
              <button data-testid="preempt-submit" onClick={submitPreempt} disabled={pBusy}
                className="w-full py-2 text-sm font-head bg-[#F59E0B] hover:bg-[#d97706] text-black font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {pBusy && <Loader2 size={14} className="animate-spin" />} Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      )}
      {reassign && (
        <div data-testid="reassign-modal" className="fixed inset-0 z-[999] flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setReassign(null)}>
          <div className="mt-20 w-full max-w-md bg-[#0d0e11] border border-[#2C2D35]" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#2C2D35] flex items-center gap-2 font-head text-sm text-white">
              <ArrowRightLeft size={15} className="text-[#8B5CF6]" /> Call Handoff — {inc.cc_number}
              <button data-testid="reassign-close" onClick={() => setReassign(null)} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">From Unit (released)</label>
                <select data-testid="reassign-from" value={reassign.from} onChange={(e) => setReassign({ ...reassign, from: e.target.value })}
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm font-mono outline-none focus:border-[#8B5CF6]">
                  <option value="">— none / add only —</option>
                  {(inc.assigned_units || []).map((u) => <option key={u} value={u}>{u}</option>)}
                </select></div>
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">To Unit (receives call) *</label>
                <select data-testid="reassign-to" value={reassign.to} onChange={(e) => setReassign({ ...reassign, to: e.target.value })}
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm font-mono outline-none focus:border-[#8B5CF6]">
                  <option value="">— select unit —</option>
                  {raUnits.filter((u) => u.unit_id !== reassign.from).map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_id} · {u.status}{u.personnel ? ` · ${u.personnel}` : ""}</option>)}
                </select></div>
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Handoff Note (audited) *</label>
                <textarea data-testid="reassign-note" value={reassign.note} onChange={(e) => setReassign({ ...reassign, note: e.target.value })} rows={3}
                  placeholder="Reason for handoff / instructions for the receiving unit…"
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#8B5CF6]" /></div>
              <button data-testid="reassign-submit" onClick={submitReassign} disabled={raBusy}
                className="w-full py-2 text-sm font-head bg-[#8B5CF6] hover:bg-[#7c3aed] text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {raBusy && <Loader2 size={14} className="animate-spin" />} Confirm Handoff
              </button>
            </div>
          </div>
        </div>
      )}
      <FieldFormModal type={formType} open={!!formType} onClose={() => setFormType(null)}
        incidentCc={inc.cc_number} defaultState={inc.location?.jurisdiction === "PA" ? "PA" : ""}
        onSubmitted={() => load()} />
    </Layout>
  );
}
